// frontend/admin-portal/src/pages/Settings.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { PageHeader, Card, Alert, Spinner } from "@fiberhome/shared-ui";
import { useSettings } from "../contexts/SettingsContext";
import SettingsTabs from "../components/settings/SettingsTabs";
import SettingsHeader from "../components/settings/SettingsHeader";
import SettingsPanel from "../components/settings/SettingsPanel";
import GeneralSettings from "../components/settings/sections/GeneralSettings";
import FinanceSettings from "../components/settings/sections/FinanceSettings";
import ExchangeRatesSettings from "../components/settings/sections/ExchangeRatesSettings";
import PaymentsSettings from "../components/settings/sections/PaymentsSettings";
import InventorySettings from "../components/settings/sections/InventorySettings";
import FiberDefaultsSettings from "../components/settings/sections/FiberDefaultsSettings";
import AppearanceSettings from "../components/settings/sections/AppearanceSettings";
import NotificationsSettings from "../components/settings/sections/NotificationsSettings";

const TABS = [
  { id: "general", label: "General", Component: GeneralSettings },
  { id: "finance", label: "Finance", Component: FinanceSettings },
  { id: "exchange", label: "Exchange Rates", Component: ExchangeRatesSettings },
  { id: "payments", label: "Payments", Component: PaymentsSettings },
  { id: "inventory", label: "Inventory", Component: InventorySettings },
  { id: "fiber", label: "Fiber Defaults", Component: FiberDefaultsSettings },
  { id: "appearance", label: "Appearance", Component: AppearanceSettings },
  {
    id: "notifications",
    label: "Notifications",
    Component: NotificationsSettings,
  },
];

export default function Settings() {
  const { settings, loading, error, updateMany } = useSettings();
  const [active, setActive] = useState("general");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    setDraft({ ...settings });
  }, [settings]);

  const dirty = useMemo(
    () => Object.keys(draft).some((k) => draft[k] !== settings[k]),
    [draft, settings],
  );

  const handleChange = useCallback((key, value) => {
    if (typeof key !== "string") {
      // eslint-disable-next-line no-console
      console.warn("[Settings] handleChange: key must be a string, got", key);
      return;
    }
    setSaved(false);
    setSaveError("");
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const handleReset = () => setDraft({ ...settings });

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError("");
    try {
      const patch = {};
      for (const k of Object.keys(draft)) {
        if (draft[k] !== settings[k]) patch[k] = draft[k];
      }
      if (Object.keys(patch).length > 0) await updateMany(patch);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const ActiveComponent = TABS.find((t) => t.id === active)?.Component;

  if (loading && Object.keys(settings).length === 0) {
    return (
      <>
        <PageHeader title="Settings" breadcrumbs={["Home", "Settings"]} />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Configure how FiberHome behaves"
        breadcrumbs={["Home", "Settings"]}
        actions={
          <SettingsHeader
            saving={saving}
            dirty={dirty}
            onSave={handleSave}
            onReset={handleReset}
          />
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      {saveError && (
        <Alert variant="danger" className="mb-3">
          {saveError}
        </Alert>
      )}
      {saved && (
        <Alert variant="success" className="mb-3">
          Settings saved successfully.
        </Alert>
      )}

      <SettingsTabs tabs={TABS} active={active} onChange={setActive} />

      <Card className="mt-3">
        <SettingsPanel>
          {ActiveComponent ? (
            <ActiveComponent values={draft} onChange={handleChange} />
          ) : (
            <div className="text-muted">Section not found.</div>
          )}
        </SettingsPanel>
      </Card>
    </>
  );
}
